// Light or dark: an attribute on <html> (data-theme) that switches every token in globals.css,
// so the sign-in page and the shell share it without a provider. Saved per browser; the first
// visit follows the OS. `?theme=` overrides it, which the screenshot script uses.
import { useSyncExternalStore } from "react";

export type Mode = "light" | "dark";

const KEY = "ocs.theme";
const listeners = new Set<() => void>();

function initial(): Mode {
  const fromUrl = new URLSearchParams(window.location.search).get("theme");
  if (fromUrl === "light" || fromUrl === "dark") return fromUrl;
  try {
    const saved = localStorage.getItem(KEY);
    if (saved === "light" || saved === "dark") return saved;
  } catch {
    // Storage can be blocked (private mode); fall through to the OS setting.
  }
  return matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

let current: Mode | undefined;

function apply(mode: Mode): void {
  const root = document.documentElement;
  root.dataset.theme = mode;
  root.classList.toggle("dark", mode === "dark");
}

export function getMode(): Mode {
  if (current === undefined) {
    current = initial();
    apply(current);
  }
  return current;
}

export function setMode(mode: Mode): void {
  current = mode;
  apply(mode);
  try {
    localStorage.setItem(KEY, mode);
  } catch {
    // Not persisting the choice is fine.
  }
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useMode(): Mode {
  return useSyncExternalStore(subscribe, getMode);
}
